<template>
    <section v-if="panel" class="regulatory-panel" aria-labelledby="regulatory-panel-title">
        <div class="regulatory-panel__inner">
            <h2 id="regulatory-panel-title" class="regulatory-panel__title">{{ panel.title }}</h2>

            <BondV2SignupBlurGate
                :message="regulatoryGateMessage"
                @signup-required="handleSignupGate"
            >
                <ul class="regulatory-panel__docs" aria-label="Regulatory documents">
                    <li v-for="doc in panel.documents" :key="doc.documentId || doc.label" class="regulatory-doc">
                        <span>{{ doc.label }}</span>
                        <a
                            v-if="doc.fileUrl"
                            class="regulatory-doc__download"
                            :href="doc.fileUrl"
                            target="_blank"
                            rel="noopener noreferrer"
                            :aria-label="`Download ${doc.label}`"
                        >
                            <img :src="downloadIcon" alt="" aria-hidden="true" />
                        </a>
                        <button v-else type="button" class="regulatory-doc__download" :aria-label="`${doc.label} unavailable`" disabled>
                            <img :src="downloadIcon" alt="" aria-hidden="true" />
                        </button>
                    </li>
                </ul>
            </BondV2SignupBlurGate>
        </div>
    </section>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import downloadIcon from '@/assets/icons/bond-details-v2/regulatory-download.svg'
import BondV2SignupBlurGate from './BondV2SignupBlurGate.vue'
import { useBondV2Data } from './bondV2Context'

interface SignupGatePayload {
    title: string
    message: string
    ctaName: string
}

const data = useBondV2Data()
const panel = computed(() => data.value.regulatoryAssurance)
const emit = defineEmits<{
    'signup-required': [payload: SignupGatePayload]
}>()

const regulatoryGateMessage = computed(() => `Sign up to view ${panel.value?.title || 'Regulatory Assurance Panel'}`)

const handleSignupGate = () => {
    const sectionTitle = panel.value?.title || 'Regulatory Assurance Panel'
    emit('signup-required', {
        title: `Sign Up to View ${sectionTitle}`,
        message: `Sign up to access ${sectionTitle} and diligence documents.`,
        ctaName: 'signup-regulatory-assurance-bond-details'
    })
}
</script>

<style scoped>
.regulatory-panel {
    width: 100%;
    background: #fff;
    border: 1px solid #eaf7ff;
    border-radius: 8px;
    padding: 17px 20px;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    justify-content: flex-start;
    min-width: 0;
    box-sizing: border-box;
}

.regulatory-panel__inner {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.regulatory-panel__title {
    margin: 0;
    color: #222;
    font-family: Inter, var(--font-family-primary);
    font-size: 15px;
    font-weight: 700;
    line-height: 24px;
}

.regulatory-panel__docs {
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin: 0;
    padding: 0;
    list-style: none;
    min-height: 330px;
}

.regulatory-doc {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    min-height: 39px;
    padding: 8px 10px;
    min-width: 0;
}

.regulatory-doc:nth-child(even) {
    background: #f9fbff;
}

.regulatory-doc span {
    flex: 1 1 auto;
    min-width: 0;
    color: #5b6684;
    font-family: Inter, var(--font-family-primary);
    font-size: 15px;
    font-weight: 500;
    line-height: 22.4px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.regulatory-doc__download {
    width: 24px;
    height: 24px;
    flex: 0 0 24px;
    padding: 0;
    border: none;
    background: transparent;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
}

.regulatory-doc__download:disabled {
    cursor: not-allowed;
    opacity: 0.45;
}

.regulatory-doc__download img {
    width: 24px;
    height: 24px;
    display: block;
}

@media (max-width: 640px) {
    .regulatory-panel {
        border-radius: 0;
        padding: 14px 16px;
        box-sizing: border-box;
    }

    .regulatory-panel__docs {
        min-height: 260px;
    }

    .regulatory-panel__title {
        font-size: 14px;
        line-height: 20px;
    }

    .regulatory-doc {
        gap: 8px;
        padding: 8px;
    }

    .regulatory-doc span {
        min-width: 0;
        font-size: 13px;
        line-height: 18px;
        display: -webkit-box;
        -webkit-box-orient: vertical;
        -webkit-line-clamp: 2;
        white-space: normal;
    }
}
</style>
